interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheConfig {
  ttl: number; // Default TTL in milliseconds (5 minutes)
  maxSize: number; // Maximum number of items in cache
}

function getStorageNamespace(): string {
  const explicit = (import.meta.env.VITE_STORAGE_NAMESPACE as string | undefined)?.trim();
  if (explicit) return explicit.replace(/[^a-zA-Z0-9_-]/g, '_');

  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
  if (supabaseUrl) {
    try {
      return new URL(supabaseUrl).host.replace(/[^a-zA-Z0-9_-]/g, '_');
    } catch {
      return supabaseUrl.replace(/[^a-zA-Z0-9_-]/g, '_');
    }
  }

  return 'default';
}

const STORAGE_NAMESPACE = getStorageNamespace();

class LocalStorageCache {
  private config: CacheConfig;
  private prefix: string;

  constructor(prefix: string = 'search_cache', config: Partial<CacheConfig> = {}) {
    this.prefix = `${STORAGE_NAMESPACE}_${prefix}`;
    this.config = {
      ttl: 5 * 60 * 1000, // 5 minutes default
      maxSize: 100, // Max 100 items
      ...config
    };
  }

  private getKey(key: string): string {
    return `${this.prefix}_${key}`;
  }

  set<T>(key: string, data: T, ttl?: number): void {
    try {
      const cacheKey = this.getKey(key);
      const item: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        ttl: ttl || this.config.ttl
      };

      localStorage.setItem(cacheKey, JSON.stringify(item));
      this.cleanup();
    } catch (error) {
      console.warn('Failed to set cache item:', error);
    }
  }

  get<T>(key: string): T | null {
    try {
      const cacheKey = this.getKey(key);
      const item = localStorage.getItem(cacheKey);
      
      if (!item) return null;

      const cacheItem: CacheItem<T> = JSON.parse(item);
      const now = Date.now();

      // Check if item has expired
      if (now - cacheItem.timestamp > cacheItem.ttl) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.warn('Failed to get cache item:', error);
      return null;
    }
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  remove(key: string): void {
    try {
      const cacheKey = this.getKey(key);
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Failed to remove cache item:', error);
    }
  }

  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  private cleanup(): void {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.prefix));
      
      if (cacheKeys.length <= this.config.maxSize) return;

      // Get all cache items with their timestamps
      const items = cacheKeys.map(key => {
        try {
          const item = localStorage.getItem(key);
          if (!item) return null;
          
          const cacheItem = JSON.parse(item);
          return {
            key,
            timestamp: cacheItem.timestamp
          };
        } catch {
          return null;
        }
      }).filter(Boolean);

      // Sort by timestamp (oldest first) and remove excess items
      items.sort((a, b) => a!.timestamp - b!.timestamp);
      const toRemove = items.slice(0, items.length - this.config.maxSize);
      
      toRemove.forEach(item => {
        localStorage.removeItem(item!.key);
      });
    } catch (error) {
      console.warn('Failed to cleanup cache:', error);
    }
  }

  getStats(): { size: number; keys: string[] } {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.prefix));
      return {
        size: cacheKeys.length,
        keys: cacheKeys.map(key => key.replace(this.prefix + '_', ''))
      };
    } catch (error) {
      console.warn('Failed to get cache stats:', error);
      return { size: 0, keys: [] };
    }
  }
}

/**
 * Local Data Storage for Full Datasets
 * Perfect for 5-person teams - stores complete datasets locally with smart sync
 */
class LocalDataStorage {
  private prefix: string;
  private syncInterval: number = 5 * 60 * 1000; // 5 minutes sync interval
  private lastSyncKey: string;
  private dataKey: string;
  private lastModifiedKey: string;

  constructor(prefix: string) {
    this.prefix = `${STORAGE_NAMESPACE}_${prefix}`;
    this.lastSyncKey = `${prefix}_last_sync`;
    this.dataKey = `${prefix}_data`;
    this.lastModifiedKey = `${prefix}_last_modified`;
  }

  // Store complete dataset locally
  setData<T>(data: T[]): void {
    try {
      const timestamp = Date.now();
      
      // Store the data
      localStorage.setItem(this.dataKey, JSON.stringify(data));
      
      // Store last modified timestamp
      localStorage.setItem(this.lastModifiedKey, timestamp.toString());
    } catch (error) {
      console.warn(`Failed to store ${this.prefix} data locally:`, error);
    }
  }

  // Get complete dataset from local storage
  getData<T>(): T[] | null {
    try {
      const data = localStorage.getItem(this.dataKey);
      if (!data) return null;
      
      return JSON.parse(data);
    } catch (error) {
      console.warn(`Failed to get ${this.prefix} data from local storage:`, error);
      return null;
    }
  }

  // Check if local data exists and is fresh
  hasFreshData(maxAge: number = 30 * 60 * 1000): boolean {
    try {
      const lastModified = localStorage.getItem(this.lastModifiedKey);
      if (!lastModified) return false;
      
      const age = Date.now() - parseInt(lastModified);
      return age < maxAge;
    } catch (error) {
      return false;
    }
  }

  // Check if any cached data exists (regardless of age) - useful for outage fallback
  hasData(): boolean {
    try {
      const data = localStorage.getItem(this.dataKey);
      return data !== null && data !== '';
    } catch (error) {
      return false;
    }
  }

  // Get last sync timestamp
  getLastSync(): number {
    try {
      const lastSync = localStorage.getItem(this.lastSyncKey);
      return lastSync ? parseInt(lastSync) : 0;
    } catch (error) {
      return 0;
    }
  }

  // Update last sync timestamp
  updateLastSync(): void {
    try {
      localStorage.setItem(this.lastSyncKey, Date.now().toString());
    } catch (error) {
      console.warn(`Failed to update ${this.prefix} last sync:`, error);
    }
  }

  // Check if sync is needed
  needsSync(syncInterval: number = this.syncInterval): boolean {
    const lastSync = this.getLastSync();
    return Date.now() - lastSync > syncInterval;
  }

  // Clear local data
  clear(): void {
    try {
      localStorage.removeItem(this.dataKey);
      localStorage.removeItem(this.lastModifiedKey);
      localStorage.removeItem(this.lastSyncKey);
    } catch (error) {
      console.warn(`Failed to clear ${this.prefix} data:`, error);
    }
  }

  // Alias for clear() for consistency
  clearData(): void {
    this.clear();
  }

  // Get storage stats
  getStats(): { hasData: boolean; itemCount: number; lastModified: number; lastSync: number } {
    try {
      const data = this.getData();
      const lastModified = localStorage.getItem(this.lastModifiedKey);
      const lastSync = this.getLastSync();
      
      return {
        hasData: data !== null,
        itemCount: data ? data.length : 0,
        lastModified: lastModified ? parseInt(lastModified) : 0,
        lastSync: lastSync
      };
    } catch (error) {
      return { hasData: false, itemCount: 0, lastModified: 0, lastSync: 0 };
    }
  }
}

// Create specific cache instances for different data types
export const searchCache = new LocalStorageCache('search', {
  ttl: 10 * 60 * 1000, // 10 minutes for search data
  maxSize: 50
});

export const customerCache = new LocalStorageCache('customers', {
  ttl: 15 * 60 * 1000, // 15 minutes for customer data
  maxSize: 20
});

export const bookingCache = new LocalStorageCache('bookings', {
  ttl: 5 * 60 * 1000, // 5 minutes for booking data
  maxSize: 30
});

export const instructorCache = new LocalStorageCache('instructors', {
  ttl: 30 * 60 * 1000, // 30 minutes for instructor data
  maxSize: 10
});

// Create local data storage instances for full datasets
export const localCustomerStorage = new LocalDataStorage('customers_full');
export const localBookingStorage = new LocalDataStorage('bookings_full');
export const localAttendanceStorage = new LocalDataStorage('attendance_full');

export default LocalStorageCache;
